<template>
  <div>
    <div v-if="loading" class="loading-container">
      <i class="fa fa-cog fa-spin"></i>
      <span>Daten werden geladen...</span>
    </div>
    <section class="dashboard" v-if="!loading">
      <div class="page-header">
        <h3 class="page-title">Rechnungen ({{ totalRows }}) - {{ openCount }} offen ({{ (openSum / 100).toFixed(2) }} Euro)</h3>
      </div>

      <!-- CSV Payment Matching Section -->
      <div class="row mb-3">
        <div class="col-12">
          <div class="card">
            <div class="card-body">
              <div class="csv-header" @click="csvExpanded = !csvExpanded" style="cursor: pointer;">
                <h5 class="mb-0">
                  <i :class="csvExpanded ? 'fa fa-chevron-down' : 'fa fa-chevron-right'" style="margin-right: 8px;"></i>
                  CSV-Zahlung abgleichen
                </h5>
              </div>

              <div v-if="csvExpanded" style="margin-top: 15px;">
                <div class="d-flex align-items-center" style="gap: 10px;">
                  <b-form-file
                    v-model="csvFile"
                    accept=".csv"
                    placeholder="CSV-Datei auswählen..."
                    drop-placeholder="Datei hier ablegen..."
                    style="max-width: 400px;"
                  ></b-form-file>
                  <b-button variant="primary" @click="matchInvoices" :disabled="!csvFile || csvLoading">
                    <i v-if="csvLoading" class="fa fa-cog fa-spin"></i>
                    {{ csvLoading ? 'Wird verarbeitet...' : 'Abgleichen' }}
                  </b-button>
                  <b-button v-if="csvResults" variant="outline-secondary" @click="resetCsv">Zurücksetzen</b-button>
                </div>

                <!-- Results -->
                <div v-if="csvResults" style="margin-top: 20px;">
                  <!-- Matched invoices -->
                  <h6>Gefundene Rechnungen ({{ csvResults.matched.length }})</h6>
                  <div v-if="csvResults.matched.length > 0">
                    <div class="d-flex align-items-center mb-2" style="gap: 10px;">
                      <label class="mb-0" style="white-space: nowrap;">
                        <input type="checkbox" v-model="selectAllMatched" @change="toggleSelectAll" />
                        Alle auswählen
                      </label>
                      <b-button
                        variant="success"
                        size="sm"
                        @click="markSelectedAsPaid"
                        :disabled="selectedCount === 0 || isProcessing"
                      >
                        <i v-if="isProcessing" class="fa fa-cog fa-spin"></i>
                        {{ isProcessing ? 'Wird markiert...' : 'Als bezahlt markieren (' + selectedCount + ')' }}
                      </b-button>
                    </div>

                    <!-- Progress bar -->
                    <b-progress v-if="isProcessing" :value="processProgress" :max="processTotal" show-progress animated class="mb-2"></b-progress>

                    <div class="table-responsive">
                      <table class="table table-hover table-sm">
                        <thead>
                          <tr>
                            <th></th>
                            <th>RE-Nr</th>
                            <th>Name</th>
                            <th>CSV-Datum</th>
                            <th>CSV-Betrag</th>
                            <th>Rechnungsbetrag (Netto)</th>
                            <th>Rechnungsbetrag (Brutto)</th>
                            <th>Status</th>
                            <th>Hinweis</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(match, idx) in csvResults.matched" :key="'m-' + idx" :class="{ 'table-success': match.billing.paid }">
                            <td>
                              <input type="checkbox" v-model="match.selected" :disabled="match.billing.paid" />
                            </td>
                            <td>{{ match.invoiceNr }}</td>
                            <td>{{ match.csvName }}</td>
                            <td>{{ match.csvDate }}</td>
                            <td>{{ match.csvAmount }} EUR</td>
                            <td>
                              <div class="billing-modal-btn" v-b-modal.modalmd @click="openModal(match.billing)">
                                {{ (match.billing.invoice_amount / 100).toFixed(2) }} EUR
                              </div>
                            </td>
                            <td>
                              <div class="billing-modal-btn" v-b-modal.modalmd @click="openModal(match.billing)">
                                {{ (match.billing.invoice_amount / 100 * 1.19).toFixed(2) }} EUR
                              </div>
                            </td>
                            <td>
                              <span v-if="match.billing.paid" class="text-success">
                                <i class="fa fa-check"></i> Bereits bezahlt
                              </span>
                              <span v-else class="text-warning">Offen</span>
                            </td>
                            <td>
                              <span v-if="match.amountWarning" class="text-warning">
                                <i class="fa fa-exclamation-triangle"></i> Betrag weicht ab
                              </span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div v-else class="text-muted mb-3">Keine Rechnungen zugeordnet.</div>

                  <!-- Unmatched entries -->
                  <h6 style="margin-top: 20px;">Nicht zugeordnet ({{ csvResults.unmatched.length }})</h6>
                  <div v-if="csvResults.unmatched.length > 0">
                    <div class="table-responsive">
                      <table class="table table-hover table-sm">
                        <thead>
                          <tr>
                            <th>Zeile</th>
                            <th>RE-Nr</th>
                            <th>Name</th>
                            <th>Verwendungszweck</th>
                            <th>Betrag</th>
                            <th>Grund</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(item, idx) in csvResults.unmatched" :key="'u-' + idx">
                            <td>{{ item.row }}</td>
                            <td>{{ item.invoiceNr || '-' }}</td>
                            <td>{{ item.name }}</td>
                            <td class="csv-purpose-cell">{{ item.purpose }}</td>
                            <td>{{ item.amount }} EUR</td>
                            <td>{{ item.reason }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div v-else class="text-muted">Alle Zahlungen konnten zugeordnet werden.</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-12 grid-margin">
          <div class="card">
            <div class="card-body">
              <!--
                            <b-form-select v-model="stateFilter" :options="account_states" style="margin-bottom:20px;" />
        -->
              <div style="margin-bottom: 10px">
                <label>
                  <input v-model="invoice_open" type="checkbox" data-toggle="toggle" />
                  offene Rechnungen
                </label>
                <label style="margin-left: 20px">
                  <input v-model="invoice_overdue_only" type="checkbox" data-toggle="toggle" />
                  nur älter als 30 Tage (Zahlungsziel abgelaufen)
                </label>
              </div>

              <form class="forms-sample">
                <b-form-group label="" label-for="input1">
                  <b-form-input v-model="invoicefilter" type="text" id="input1"
                    placeholder="Rechnungsnummer"></b-form-input>
                </b-form-group>
              </form>

              <div class="table-responsive">
                <table class="table table-hover">
                  <thead>
                    <tr>
                      <th class="sortable-th" @click="sortBy('invoice_nr')">Nr. <i :class="sortIcon('invoice_nr')"></i></th>
                      <th class="sortable-th" @click="sortBy('invoice_date')">Datum <i :class="sortIcon('invoice_date')"></i></th>
                      <th>Zeitraum</th>
                      <th class="sortable-th" @click="sortBy('emailsend_at')">E-Mail <i :class="sortIcon('emailsend_at')"></i></th>
                      <th class="sortable-th" @click="sortBy('invoice_amount')">Netto <i :class="sortIcon('invoice_amount')"></i></th>
                      <th class="sortable-th" @click="sortBy('invoice_amount')">Brutto <i :class="sortIcon('invoice_amount')"></i></th>
                      <th class="sortable-th" @click="sortBy('paid')">Bezahlt <i :class="sortIcon('paid')"></i></th>
                      <th></th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in filteredBillings" :key="item.uid">
                      <td>
                        <a v-if="item.download_file" :href="item.download_file" target="_blank">{{ item.invoice_nr
                          }}</a>
                      </td>
                      <td>{{ item.invoice_date }}</td>
                      <td>
                        {{ item.invoice_range_from }}<br />{{
                          item.invoice_range_to
                        }}
                      </td>
                      <td>
                        <div v-if="item.emailsend_at">
                          <i class="fa fa-envelope-o" aria-hidden="true"></i>
                          {{ item.emailsend_at }}
                        </div>
                      </td>
                      <td>
                        <div class="billing-modal-btn" v-b-modal.modalmd @click="openModal(item)">
                          {{ parseFloat((item.invoice_amount / 100).toFixed(2)) }} Euro
                        </div>
                      </td>
                      <td>
                        <div class="billing-modal-btn" v-b-modal.modalmd @click="openModal(item)">
                          {{ parseFloat((item.invoice_amount / 100 * 1.19).toFixed(2)) }} Euro
                        </div>
                      </td>
                      <td>
                        <i v-if="item.paid" class="fa fa-check" aria-hidden="true"></i>
                        <span v-if="item.paid">{{ item.paid_at }}</span>
                      </td>
                      <td>

                        <div v-for="tag in item.tags" :key="tag" class="badge-container">
                          <span class="badge-tag">{{ tag }}</span>
                        </div>


                      </td>

                      <td style="text-align: right">
                        <router-link target="_blank" class="btn btn-outline-primary btn-fw" :to="{
                          name: 'location-detail',
                          params: { uid: item.location_uid },
                        }">Account</router-link>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-if="filteredBillings && filteredBillings.length === 0" class="empty-state">
                <i class="mdi mdi-file-document-outline"></i>
                <p>Keine Rechnungen gefunden.</p>
              </div>

              <div class="d-flex justify-content-between align-items-center mt-3">
                <b-button
                  v-if="hasMore"
                  variant="outline-primary"
                  @click="loadMore"
                  :disabled="loadingMore"
                >
                  <i v-if="loadingMore" class="fa fa-cog fa-spin"></i>
                  {{ loadingMore ? 'Laden...' : 'Mehr anzeigen' }}
                </b-button>
                <span v-else></span>
                <span class="text-muted">{{ allBillings.length }} von {{ totalRows }} Rechnungen</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <b-modal id="modalmd" title="Details" size="md" ok-only @>
        <form class="forms-sample">
          <!--
          <b-form-group label="Rechnung verschickt" label-for="emailsend">
            <input
              type="checkbox"
              id="emailsend"
              name="emailsend"
              v-model="current_billing.emailsend"
            />
          </b-form-group>
          -->
          <b-form-group label="Rechnung verschickt" label-for="">
            <date-picker v-model="current_billing.emailsend_at" valueType="format" :format="dateFormat"
              placeholder="Datum wählen"></date-picker>
          </b-form-group>

          <!--
          <b-form-group
            label="Bezahlt"
            label-for="paid"
            style="margin-top: 30px"
          >
            <input
              type="checkbox"
              id="paid"
              name="paid"
              v-model="current_billing.paid"
            />
          </b-form-group>
          -->
          <b-form-group label="Bezahlt am" label-for="">
            <date-picker v-model="current_billing.paid_at" valueType="format" :format="dateFormat"
              placeholder="Datum wählen"></date-picker>
          </b-form-group>

          <b-form-group label="Tags" label-for="">
            <b-form-tags input-id="tags-basic" placeholder="" v-model="current_billing.tags"></b-form-tags>
          </b-form-group>

          <b-button variant="success" @click="saveEmailDetails()" class="mr-2">Speichern</b-button>
          <b-alert v-if="modalSaved1" variant="primary" style="margin-top: 20px" show>Gespeichert</b-alert>
        </form>
      </b-modal>
    </section>
  </div>
</template>


<script>
import moment from "moment";
import { updateInvoiceInfo } from "@/services/locations.service";

import { getAllBillings, searchBillingsByInvoiceNrs } from "@/services/billings.service";
import DatePicker from "vue2-datepicker";

export default {
  name: "dashboard",
  components: {
    DatePicker,
  },
  data() {
    return {
      time1: null,
      loading: true,
      current_billing: {},
      modalSaved1: false,
      dateFormat: "DD.MM.YYYY",
      invoicefilter: null,
      invoice_open: true,
      invoice_overdue_only: true,
      sortKey: 'name',
      sortOrder: 'asc',
      perPage: 500,
      totalRows: 0,
      openCount: 0,
      openSum: 0,
      loadingMore: false,
      // CSV matching
      csvExpanded: false,
      csvFile: null,
      csvResults: null,
      csvLoading: false,
      selectAllMatched: false,
      isProcessing: false,
      processProgress: 0,
      processTotal: 0,
    };
  },
  computed: {
    allBillings: function () {
      return this.$store.state.billings || [];
    },
    hasMore: function () {
      return this.allBillings.length < this.totalRows;
    },
    selectedCount: function () {
      if (!this.csvResults) return 0;
      return this.csvResults.matched.filter(function (m) { return m.selected && !m.billing.paid; }).length;
    },
    sum_billings: function () {
      const bill = this.$store.state.billings || []

      let sum = 0

      for (let i = 0; i < bill.length; i++) {
        if (bill[i].invoice_amount) {
          sum += bill[i].invoice_amount
        }
      }

      return sum / 100
    },
    filteredBillings: function () {
      let billings = this.$store.state.billings || [];

      if (this.invoice_open) {
        billings = billings.filter((item) => !item.paid);
      }

      // Zahlungsziel: Rechnungen juenger als 30 Tage ausblenden
      // (invoice_date DD.MM.YYYY; ohne parsebares Datum sichtbar lassen)
      if (this.invoice_overdue_only) {
        billings = billings.filter((item) => {
          const invoiceDate = moment(item.invoice_date, 'DD.MM.YYYY', true);
          if (!invoiceDate.isValid()) return true;
          return moment().diff(invoiceDate, 'days') >= 30;
        });
      }

      if (this.invoicefilter && this.invoicefilter.length > 1) {
        billings = billings.filter((item) =>
          item.invoice_nr.toString().indexOf(this.invoicefilter) != -1
        );
      }

      if (this.sortKey) {
        billings = billings.slice().sort((a, b) => {
          let valA = a[this.sortKey];
          let valB = b[this.sortKey];

          if (typeof valA === 'string' && typeof valB === 'string') {
            valA = valA.toLowerCase();
            valB = valB.toLowerCase();
          }

          if (this.sortOrder === 'asc') {
            return valA > valB ? 1 : valA < valB ? -1 : 0;
          } else {
            return valA < valB ? 1 : valA > valB ? -1 : 0;
          }
        });
      }

      return billings;
    },
  },
  mounted() {
    this.loadBillings();
  },
  methods: {
    loadBillings: function () {
      this.loading = true;

      getAllBillings(this.perPage, 0).then((resp) => {
        if (resp.data.error) {
          this.$router.push({ name: "locations" });
        } else {
          this.$store.state.billings = resp.data;
          this.totalRows = resp.total || resp.max || 0;
          this.openCount = resp.open_count || 0;
          this.openSum = resp.open_sum || 0;
          this.loading = false;
        }
      }).catch(() => {
        localStorage.removeItem("token");
        this.$router.push({ name: "login" });
      });
    },
    loadMore: function () {
      this.loadingMore = true;
      var offset = this.allBillings.length;

      getAllBillings(this.perPage, offset).then((resp) => {
        if (resp.data && !resp.data.error) {
          this.$store.state.billings = this.allBillings.concat(resp.data);
          this.totalRows = resp.total || resp.max || this.totalRows;
          if (resp.open_count !== undefined) this.openCount = resp.open_count;
          if (resp.open_sum !== undefined) this.openSum = resp.open_sum;
        }
        this.loadingMore = false;
      }).catch(() => {
        this.loadingMore = false;
      });
    },
    sortIcon: function (key) {
      if (this.sortKey !== key) return 'fa fa-sort sort-icon sort-icon-inactive';
      return this.sortOrder === 'asc' ? 'fa fa-sort-asc sort-icon' : 'fa fa-sort-desc sort-icon';
    },
    sortBy: function (key) {
      if (this.sortKey === key) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortKey = key;
        this.sortOrder = 'asc';
      }
    },
    saveEmailDetails: function () {
      if (this.current_billing.paid_at) {
        this.current_billing.paid = true;
      } else {
        this.current_billing.paid = false;
      }

      if (this.current_billing.emailsend_at) {
        this.current_billing.emailsend = true;
      } else {
        this.current_billing.emailsend = false;
      }


      updateInvoiceInfo({
        current_item: this.current_billing,
      }).then((data) => {
        this.modalSaved1 = true;
        console.log(data);
      });
    },

    openModal: function (item) {
      this.modalSaved1 = false;
      this.current_billing = item;
    },

    // CSV Matching Methods

    parseCSV: function (text) {
      var lines = text.split('\n');
      if (lines.length < 2) return [];

      var headers = lines[0].split(';').map(function (h) { return h.trim(); });
      var rows = [];

      for (var i = 1; i < lines.length; i++) {
        var line = lines[i].trim();
        if (!line) continue;

        var values = line.split(';');
        var row = {};
        for (var j = 0; j < headers.length; j++) {
          row[headers[j]] = (values[j] || '').trim();
        }
        row._lineNumber = i + 1;
        rows.push(row);
      }

      return rows;
    },

    extractInvoiceNumbers: function (text) {
      if (!text) return [];
      var regex = /R\s*E\s*-?\s*(\d+)/gi;
      var matches = [];
      var match;
      while ((match = regex.exec(text)) !== null) {
        matches.push('RE-' + match[1]);
      }
      return matches;
    },

    parseGermanAmount: function (str) {
      if (!str) return 0;
      // Remove thousands separator (dots), replace comma with dot
      var cleaned = str.replace(/\./g, '').replace(',', '.');
      var val = parseFloat(cleaned);
      return isNaN(val) ? 0 : val;
    },

    matchInvoices: function () {
      var self = this;
      if (!this.csvFile) return;

      this.csvLoading = true;
      this.csvResults = null;

      var reader = new FileReader();
      reader.onload = function (e) {
        var text = e.target.result;
        var csvRows = self.parseCSV(text);

        // First pass: extract all RE numbers from CSV and collect rows without RE numbers
        var allInvoiceNrs = [];
        var unmatchedNoRe = [];
        var rowsWithRe = [];

        for (var r = 0; r < csvRows.length; r++) {
          var row = csvRows[r];
          var purpose = row['Verwendungszweck'] || '';
          var csvDate = row['Datum'] || '';
          var csvAmountRaw = row['Betrag'] || '';
          var csvAmount = self.parseGermanAmount(csvAmountRaw);
          var invoiceNrs = self.extractInvoiceNumbers(purpose);

          var csvName = row['Name'] || '';

          if (invoiceNrs.length === 0) {
            unmatchedNoRe.push({
              row: row._lineNumber,
              invoiceNr: null,
              name: csvName,
              purpose: purpose,
              amount: csvAmount.toFixed(2),
              reason: 'Keine RE-Nummer gefunden'
            });
          } else {
            rowsWithRe.push({ row: row, invoiceNrs: invoiceNrs, csvDate: csvDate, csvAmount: csvAmount, csvName: csvName, purpose: purpose });
            for (var n = 0; n < invoiceNrs.length; n++) {
              if (allInvoiceNrs.indexOf(invoiceNrs[n]) === -1) {
                allInvoiceNrs.push(invoiceNrs[n]);
              }
            }
          }
        }

        if (allInvoiceNrs.length === 0) {
          self.csvResults = { matched: [], unmatched: unmatchedNoRe };
          self.csvLoading = false;
          return;
        }

        // Search only for the found RE numbers via API
        searchBillingsByInvoiceNrs(allInvoiceNrs).then(function (resp) {
          var foundBillings = resp.data || [];

          // Build lookup map
          var billingMap = {};
          for (var i = 0; i < foundBillings.length; i++) {
            var b = foundBillings[i];
            if (b.invoice_nr) {
              billingMap[b.invoice_nr] = b;
            }
          }

          var matched = [];
          var unmatched = unmatchedNoRe.slice();

          for (var r = 0; r < rowsWithRe.length; r++) {
            var entry = rowsWithRe[r];

            for (var n = 0; n < entry.invoiceNrs.length; n++) {
              var nr = entry.invoiceNrs[n];
              var nrNumeric = parseInt(String(nr).replace(/\D/g, ''), 10);
              var billing = billingMap[nrNumeric];

              if (billing) {
                var nettoAmount = billing.invoice_amount / 100;
                var bruttoAmount = nettoAmount * 1.19;
                var amountWarning = Math.abs(entry.csvAmount - nettoAmount) > 0.01 && Math.abs(entry.csvAmount - bruttoAmount) > 0.01;

                matched.push({
                  invoiceNr: nr,
                  csvName: entry.csvName,
                  csvDate: entry.csvDate,
                  csvAmount: entry.csvAmount.toFixed(2),
                  billing: billing,
                  amountWarning: amountWarning,
                  selected: !billing.paid
                });
              } else {
                unmatched.push({
                  row: entry.row._lineNumber,
                  invoiceNr: nr,
                  name: entry.csvName,
                  purpose: entry.purpose,
                  amount: entry.csvAmount.toFixed(2),
                  reason: 'Rechnung nicht im System gefunden'
                });
              }
            }
          }

          self.csvResults = { matched: matched, unmatched: unmatched };
          self.csvLoading = false;
        }).catch(function () {
          self.$toast.error('Fehler beim Laden der Rechnungen');
          self.csvLoading = false;
        });
      };

      reader.readAsText(this.csvFile);
    },

    toggleSelectAll: function () {
      if (!this.csvResults) return;
      var val = this.selectAllMatched;
      for (var i = 0; i < this.csvResults.matched.length; i++) {
        if (!this.csvResults.matched[i].billing.paid) {
          this.$set(this.csvResults.matched[i], 'selected', val);
        }
      }
    },

    resetCsv: function () {
      this.csvFile = null;
      this.csvResults = null;
      this.selectAllMatched = false;
      this.isProcessing = false;
      this.processProgress = 0;
      this.processTotal = 0;
    },

    markSelectedAsPaid: function () {
      var self = this;
      var selected = this.csvResults.matched.filter(function (m) { return m.selected && !m.billing.paid; });

      if (selected.length === 0) return;

      this.$swal({
        title: 'Bezahlt markieren?',
        text: selected.length + ' Rechnung(en) als bezahlt markieren?',
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Ja, markieren',
        cancelButtonText: 'Abbrechen'
      }).then(function (result) {
        if (!result.value) return;

        self.isProcessing = true;
        self.processProgress = 0;
        self.processTotal = selected.length;

        var successCount = 0;
        var errorCount = 0;

        function processNext(index) {
          if (index >= selected.length) {
            self.isProcessing = false;
            if (successCount > 0) {
              self.$toast.success(successCount + ' Rechnung(en) als bezahlt markiert');
            }
            if (errorCount > 0) {
              self.$toast.error(errorCount + ' Fehler beim Markieren');
            }
            self.loadBillings();
            return;
          }

          var match = selected[index];
          var billingItem = match.billing;

          updateInvoiceInfo({
            current_item: {
              // Server identifiziert ueber _id (findById) und
              // location_uid + invoice_nr (findOneAndUpdate-Selektor) —
              // ohne diese Felder ist das Update ein stilles No-op
              _id: billingItem._id,
              location_uid: billingItem.location_uid,
              invoice_nr: billingItem.invoice_nr,
              uid: billingItem.uid,
              paid: true,
              paid_at: match.csvDate,
              emailsend: billingItem.emailsend,
              emailsend_at: billingItem.emailsend_at,
              billing_address: billingItem.billing_address,
              tags: billingItem.tags || []
            }
          }).then(function (resp) {
            if (resp && resp.data && resp.data.error) {
              errorCount++;
              self.processProgress = index + 1;
              processNext(index + 1);
              return;
            }
            successCount++;
            match.billing.paid = true;
            match.billing.paid_at = match.csvDate;
            match.selected = false;
            self.processProgress = index + 1;
            processNext(index + 1);
          }).catch(function () {
            errorCount++;
            self.processProgress = index + 1;
            processNext(index + 1);
          });
        }

        processNext(0);
      });
    },
  },
};
</script>

<style scoped>
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
  color: #9c9fa6;
}

.loading-container i {
  font-size: 40px;
  margin-bottom: 12px;
}

.sortable-th {
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}

.sortable-th:hover {
  color: #198ae3;
}

.sort-icon {
  font-size: 11px;
  margin-left: 4px;
}

.sort-icon-inactive {
  opacity: 0.3;
}

.empty-state {
  text-align: center;
  padding: 40px 0;
  color: #9c9fa6;
}

.empty-state i {
  font-size: 48px;
  display: block;
  margin-bottom: 10px;
}

.billing-modal-btn {
  cursor: pointer;
  font-weight: 600;
}

.billing-modal-btn:hover {
  color: #00b3fc;
}

.badge-container {
  margin-bottom: 5px;
}

.badge-tag {
  color: #ffffff;
  background-color: #198ae3;
  padding: 4px 5px 4px 5px;
  font-size: 12px;
  display: inline-block;
}

.csv-header h5 {
  display: inline-block;
}

.csv-purpose-cell {
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
